import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBillDetailSettingsSchema } from "@features/EditBillDetailSettingsModal/useBillDetailSettingsSchema";
import { Button } from "@components/Button/Button";
import { PencilIcon } from "@components/icons/common";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useUpdateBillDetailMeterSettingsMutation } from "@services/bills/mutations";
import {
  type IBillData,
  type IBillDetail,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";

interface IEditBillDetailSettingsModalProps extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"];
  billDetail: IBillDetail;
}

interface IFormData {
  newDisplayName: string;
  newFactoryNumber?: string;
  newOptionalDeclaration?: boolean;
}

export const EditBillDetailSettingsModal: React.FC<
  IEditBillDetailSettingsModalProps
> = (props) => {
  const {
    jointBillServiceId,
    jointBillServiceAgreementId,
    billDetail,
    ...modalProps
  } = props;
  const modal = useModal(modalProps);
  const { t } = useTranslation();

  const schema = useBillDetailSettingsSchema();
  const { mutateAsync: updateBillDetailSettings, isPending } =
    useUpdateBillDetailMeterSettingsMutation(
      jointBillServiceId,
      jointBillServiceAgreementId,
      billDetail.billDetailId,
    );

  const { handleSubmit, control, setError } = useForm<IFormData>({
    defaultValues: {
      newDisplayName: billDetail.name,
      newFactoryNumber: billDetail.factoryNumber,
      newOptionalDeclaration: billDetail.optionalDeclaration,
    },
    resolver: zodResolver(schema),
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitMeterSettings: SubmitHandler<IFormData> = async (data) => {
    const response = await updateBillDetailSettings({
      newDisplayName: data.newDisplayName,
      ...(billDetail?.optionalDeclarationEditable
        ? {
            newOptionalDeclaration: data.newOptionalDeclaration,
          }
        : {}),
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      toast.success(t("editBillDetailSettingsModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: PencilIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("editBillDetailSettingsModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(submitMeterSettings)}
            className="w-full"
            isLoading={isPending}
          >
            {t("editBillDetailSettingsModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("editBillDetailSettingsModal.title")}
      className="md:max-w-xl"
    >
      <div className="flex flex-col gap-4">
        <FormInput
          name="newDisplayName"
          control={control}
          label={t(
            "editBillDetailSettingsModal.form.fields.newDisplayName.label",
          )}
        />
        <FormInput
          name="newFactoryNumber"
          control={control}
          label={t(
            "editBillDetailSettingsModal.form.fields.newFactoryNumber.label",
          )}
          disabled={true}
        />
        {billDetail.optionalDeclarationEditable && (
          <FormSelect
            name="newOptionalDeclaration"
            label={t(
              "editBillDetailSettingsModal.form.fields.newOptionalDeclaration.label",
            )}
            options={[
              {
                label: t(
                  "editBillDetailSettingsModal.form.fields.newOptionalDeclaration.options.auto",
                ),
                value: true,
              },
              {
                label: t(
                  "editBillDetailSettingsModal.form.fields.newOptionalDeclaration.options.manual",
                ),
                value: false,
              },
            ]}
            control={control}
          />
        )}
      </div>
    </Modal>
  );
};
